// Public marketing landing page for Roava.
// URL: https://roava.wgpapp.com/ (also the App Store Connect Marketing URL).
//
// Behavior preserved from previous landing: signed-in users are auto-redirected
// to /trip, so the existing native runtime is unaffected. Unauthenticated
// visitors and crawlers see the full marketing page below.

import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { Capacitor } from "@capacitor/core";
import { supabase } from "@/integrations/supabase/client";
import { isCrawlMode } from "@/lib/crawl-mode";
import { getLastRoute } from "@/lib/last-route";
import { getShellMode } from "@/lib/native/shell-mode";
import { MarketingShell } from "@/components/marketing/MarketingShell";
import roavaWordmark from "@/assets/bundled/roava-website-logo-transparent.png";
import appStoreBadge from "@/assets/app-store-badge.svg";


const APP_STORE_URL = "https://apps.apple.com/us/app/roava-journey/id6773094879";
import {
  Users,
  MapPin,
  Smartphone,
  Share2,
  Images,
  ShieldCheck,
} from "lucide-react";

const SITE_URL = "https://roava.wgpapp.com";
const OG_IMAGE = "https://roava.lovable.app/og-image.jpg?v=7";
const TITLE = "Roava — Plan the trip. Track the journey. Keep the memories.";
const DESC =
  "Roava helps private groups coordinate travel, record routes, share selected trip updates, and organize memories — even when connectivity gets spotty.";

export const Route = createFileRoute("/")({
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESC },
      { name: "theme-color", content: "#0f1115" },
      { property: "og:site_name", content: "Roava by WGP Apps" },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESC },
      { property: "og:type", content: "website" },
      { property: "og:url", content: `${SITE_URL}/` },
      { property: "og:image", content: OG_IMAGE },
      { property: "og:image:width", content: "1200" },
      { property: "og:image:height", content: "630" },
      { property: "og:image:alt", content: "Roava — private trip coordination" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: TITLE },
      { name: "twitter:description", content: DESC },
      { name: "twitter:image", content: OG_IMAGE },
    ],
    links: [{ rel: "canonical", href: `${SITE_URL}/` }],
  }),
  component: Landing,
});

function Landing() {
  const nav = useNavigate();
  const crawl = isCrawlMode();
  const [ready, setReady] = useState(false);

  // Native defense-in-depth: never render the public marketing shell inside
  // the iOS app. The native shell (RoavaBridgeViewController) already routes
  // bundled cold launches off `/` to `/offline-control`, but the JS may run
  // for a few ms between bundled didFinish and the native route push, and
  // can also be reached after a WKWebView content-process restart. This
  // effect makes `/` a no-op redirect in native: offline (or bundled-due-to-
  // remote-failure) → /offline-control, otherwise → last route or /trip.
  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return;
    let cancelled = false;
    (async () => {
      const offline = typeof navigator !== "undefined" && navigator.onLine === false;
      let bundledRemoteFailure = false;
      try {
        const snap = await getShellMode();
        bundledRemoteFailure =
          snap.mode === "bundled" &&
          (snap.modeReason === "remote-probe-failed" ||
            snap.modeReason === "remote-navigation-failed" ||
            snap.modeReason === "local-failed");
      } catch {
        /* keep defaults */
      }
      if (cancelled) return;
      const target =
        offline || bundledRemoteFailure
          ? "/offline-control"
          : (getLastRoute() ?? "/trip");
      nav({ to: target as string, replace: true });
    })();
    return () => {
      cancelled = true;
    };
  }, [nav]);

  useEffect(() => {
    if (Capacitor.isNativePlatform()) return;
    // Preserved behavior: signed-in users skip the marketing page and go
    // straight into the app. If we have a stored last-route from before a
    // WebView reload, restore that instead of bouncing to /trip.
    supabase.auth.getSession().then(({ data }) => {
      if (data.session) {
        const last = getLastRoute();
        nav({ to: (last ?? "/trip") as string, replace: true });
      } else {
        setReady(true);
      }
    });
  }, [nav]);


  if (Capacitor.isNativePlatform()) {
    // Hold a blank surface in native; the effect above will redirect away.
    return <div className="min-h-screen bg-background" />;
  }

  if (!crawl && !ready) {
    return <div className="min-h-screen bg-gradient-to-b from-secondary via-background to-background" />;
  }

  return (
    <MarketingShell>
      <Hero />
      <Features />
      <SupportStrip />
      {crawl && <CrawlerIndex />}
    </MarketingShell>
  );
}

function Hero() {
  return (
    <section className="pt-6 sm:pt-14 pb-12 sm:pb-20">
      <div className="grid sm:grid-cols-2 gap-10 items-center">
        <div>
          <p className="text-xs uppercase tracking-[0.18em] text-muted-foreground">
            Private trip coordination
          </p>
          <h1 className="mt-3 text-4xl sm:text-5xl font-bold tracking-tight leading-[1.05]">
            Plan the trip.
            <br />
            Track the journey.
            <br />
            <span className="text-primary">Keep the memories.</span>
          </h1>
          <p className="mt-5 text-base sm:text-lg text-muted-foreground leading-relaxed max-w-prose">
            Roava helps private groups coordinate travel, record routes, share selected
            trip updates, and organize memories — even when connectivity gets spotty.
          </p>
          <div className="mt-7 flex flex-wrap items-center gap-3">
            <Link
              to="/login"
              className="rounded-full bg-primary text-primary-foreground px-6 py-3 text-sm font-semibold shadow-lg shadow-primary/20 hover:opacity-90 transition"
            >
              Open the app
            </Link>
            <a
              href={APP_STORE_URL}
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Download Roava on the App Store"
              className="inline-flex items-center transition hover:opacity-85"
            >
              <img
                src={appStoreBadge}
                alt="Download on the App Store"
                className="h-[46px] w-auto"
              />
            </a>
          </div>
          <p className="mt-4 text-xs text-muted-foreground">
            Invite-only. You'll need a code from your trip organizer.
          </p>
        </div>

        <PreviewCard />
      </div>
    </section>
  );
}

function PreviewCard() {
  return (
    <div className="relative mx-auto w-full max-w-[300px]">
      <div
        aria-hidden
        className="absolute -inset-8 rounded-[3rem] bg-gradient-to-br from-primary/20 via-transparent to-primary/5 blur-2xl"
      />

      {/* iPhone frame */}
      <div className="relative mx-auto aspect-[9/19.5] w-full rounded-[3rem] bg-neutral-900 p-[10px] shadow-[0_30px_60px_-15px_rgba(0,0,0,0.45),0_0_0_1px_rgba(255,255,255,0.06)_inset]">
        {/* Side buttons */}
        <span aria-hidden className="absolute -left-[3px] top-[14%] h-7 w-[3px] rounded-l bg-neutral-800" />
        <span aria-hidden className="absolute -left-[3px] top-[22%] h-10 w-[3px] rounded-l bg-neutral-800" />
        <span aria-hidden className="absolute -left-[3px] top-[30%] h-10 w-[3px] rounded-l bg-neutral-800" />
        <span aria-hidden className="absolute -right-[3px] top-[24%] h-14 w-[3px] rounded-r bg-neutral-800" />

        {/* Screen — iOS Lock Screen */}
        <div
          className="relative h-full w-full overflow-hidden rounded-[2.4rem] text-white"
          style={{
            background:
              "linear-gradient(180deg, #1b2535 0%, #0f1626 45%, #050811 100%)",
          }}
        >
          {/* Soft wallpaper glow */}
          <div
            aria-hidden
            className="absolute -top-10 left-1/2 h-56 w-56 -translate-x-1/2 rounded-full opacity-40 blur-3xl"
            style={{ background: "radial-gradient(circle, #5b8cff 0%, transparent 70%)" }}
          />

          {/* Status bar */}
          <div className="relative z-20 flex items-center justify-between px-6 pt-2.5 text-[11px] font-semibold">
            <span>9:41</span>
            <span className="flex items-center gap-1">
              <svg width="16" height="10" viewBox="0 0 16 10" fill="currentColor"><rect x="0" y="6" width="3" height="4" rx="0.5"/><rect x="4" y="4" width="3" height="6" rx="0.5"/><rect x="8" y="2" width="3" height="8" rx="0.5"/><rect x="12" y="0" width="3" height="10" rx="0.5"/></svg>
              <svg width="22" height="10" viewBox="0 0 22 10" fill="none"><rect x="0.5" y="0.5" width="18" height="9" rx="2" stroke="currentColor"/><rect x="2" y="2" width="15" height="6" rx="1" fill="currentColor"/><rect x="19.5" y="3.5" width="1.5" height="3" rx="0.5" fill="currentColor"/></svg>
            </span>
          </div>

          {/* Dynamic Island — Roava Live Activity compact */}
          <div className="absolute left-1/2 top-[10px] z-30 flex h-[28px] -translate-x-1/2 items-center gap-2 rounded-full bg-black pl-2 pr-3">
            <span className="size-2 rounded-full bg-emerald-400 animate-pulse" />
            <span className="text-[10px] font-medium tracking-wide text-white/90">318 mi</span>
          </div>

          {/* Date + Time */}
          <div className="relative z-10 mt-7 text-center">
            <div className="text-[10px] font-medium uppercase tracking-[0.15em] text-white/70">
              Saturday, June 20
            </div>
            <div className="mt-0.5 text-[64px] leading-none font-thin tracking-tight">
              9:41
            </div>
          </div>

          {/* "Swipe up to unlock" */}
          <div className="relative z-10 mt-4 text-center text-[13px] font-medium text-white/60">
            Swipe up to unlock
          </div>

          {/* Live Activity card — bottom, matching real iOS Lock Screen position */}
          <div className="absolute bottom-[72px] left-3 right-3 z-10 rounded-[28px] bg-black/55 backdrop-blur-md border border-white/10 p-4 text-white">
            {/* Header row */}
            <div className="flex items-center justify-between">
              <span className="text-[12px] font-semibold leading-tight">Roava Tracking</span>
              <div className="flex items-center gap-1.5">
                <span className="text-[9px] uppercase tracking-wider text-white/60">Elapsed</span>
                <span className="text-[14px] font-semibold leading-none tabular-nums">02:14:38</span>
              </div>
            </div>

            {/* Status strip */}
            <div className="mt-3 flex items-stretch">
              <div className="flex-1 text-center">
                <div className="text-[8px] uppercase tracking-wider text-white/50">GPS cadence</div>
                <div className="mt-0.5 text-[11px] font-semibold">~5m</div>
              </div>
              <div className="w-px bg-white/15 mx-1" />
              <div className="flex-1 text-center">
                <div className="text-[8px] uppercase tracking-wider text-white/50">Last GPS fix</div>
                <div className="mt-0.5 text-[11px] font-semibold">just now</div>
              </div>
              <div className="w-px bg-white/15 mx-1" />
              <div className="flex-1 text-center">
                <div className="text-[8px] uppercase tracking-wider text-white/50">Sync queue</div>
                <div className="mt-0.5 text-[11px] font-semibold">Clear</div>
              </div>
            </div>

            {/* Cadence switcher */}
            <div className="mt-3">
              <div className="text-[8px] uppercase tracking-wider text-white/50 mb-1">GPS detail</div>
              <div className="flex gap-1.5">
                <span className="flex-1 rounded-md border border-white/20 bg-white/10 px-1.5 py-1 text-center text-[9px] font-semibold">Battery saver</span>
                <span className="flex-1 rounded-md border border-blue-400/40 bg-blue-500/20 px-1.5 py-1 text-center text-[9px] font-semibold text-blue-200">Balanced</span>
                <span className="flex-1 rounded-md border border-white/20 bg-white/10 px-1.5 py-1 text-center text-[9px] font-semibold">High detail</span>
              </div>
            </div>

            {/* Control row */}
            <div className="mt-3 flex gap-1.5">
              <span className="flex-1 rounded-md border border-orange-400/40 bg-orange-500/20 px-1.5 py-1.5 text-center text-[10px] font-semibold text-orange-200">
                <span className="inline-block mr-0.5">▐▌</span> Pause
              </span>
              <span className="flex-1 rounded-md border border-blue-400/40 bg-blue-500/20 px-1.5 py-1.5 text-center text-[10px] font-semibold text-blue-200">
                📍 Drop Pin
              </span>
              <span className="flex-1 rounded-md bg-red-600/80 px-1.5 py-1.5 text-center text-[10px] font-semibold text-white">
                ■ End
              </span>
            </div>
          </div>

          {/* Bottom shortcuts (flashlight / camera) */}
          <div className="absolute bottom-7 left-0 right-0 z-10 flex items-center justify-between px-7 text-white/80">
            <div className="flex size-9 items-center justify-center rounded-full bg-white/10 backdrop-blur">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M9 2h6l-1 7h-4z"/><path d="M10 9h4l-1 13h-2z"/></svg>
            </div>
            <div className="flex size-9 items-center justify-center rounded-full bg-white/10 backdrop-blur">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z"/><circle cx="12" cy="13" r="4"/></svg>
            </div>
          </div>

          {/* Home indicator */}
          <div className="absolute bottom-1.5 left-1/2 h-[5px] w-[100px] -translate-x-1/2 rounded-full bg-white/80" />
        </div>
      </div>
    </div>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-lg bg-background/60 border border-border/60 py-2">
      <div className="text-lg font-semibold">{value}</div>
      <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{label}</div>
    </div>
  );
}

function Features() {
  const items = [
    {
      icon: Users,
      title: "Invite-only trip spaces",
      body: "Each trip is a private space your organizer invites people into — no public profiles, no discovery feed.",
    },
    {
      icon: MapPin,
      title: "Offline-aware route tracking",
      body: "Routes record on-device and sync when you're back online, so the trip view stays accurate in remote areas.",
    },
    {
      icon: Smartphone,
      title: "Lock Screen & Dynamic Island controls",
      body: "Glance at trip status and pause or end tracking from the Lock Screen and Dynamic Island without unlocking your phone.",
    },
    {
      icon: Share2,
      title: "Family Follow share links",
      body: "Generate an invite-only link to share a limited trip view with family back home. Revoke it any time.",
    },
    {
      icon: Images,
      title: "Photos & video memories",
      body: "Upload photos and short videos into a trip's private library. Organize, caption, and re-live the journey together.",
    },
    {
      icon: ShieldCheck,
      title: "Private, traveler-controlled sharing",
      body: "You decide what's visible to organizers versus the wider trip group. Defaults are conservative.",
    },
  ];

  return (
    <section className="py-10 sm:py-14 border-t border-border/60">
      <header className="max-w-2xl">
        <h2 className="text-2xl sm:text-3xl font-bold tracking-tight">
          Built for real-world adventures
        </h2>
        <p className="mt-3 text-muted-foreground">
          Roava focuses on the small group of people on your trip — not the internet.
          Helpful trip visibility, not safety-critical monitoring.
        </p>
      </header>

      <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {items.map(({ icon: Icon, title, body }) => (
          <div
            key={title}
            className="rounded-2xl border border-border bg-card/60 p-5 hover:bg-card transition-colors"
          >
            <div className="inline-flex items-center justify-center size-9 rounded-xl bg-primary/10 text-primary">
              <Icon className="size-5" aria-hidden />
            </div>
            <h3 className="mt-4 text-base font-semibold">{title}</h3>
            <p className="mt-2 text-sm text-muted-foreground leading-relaxed">{body}</p>
          </div>
        ))}
      </div>
    </section>
  );
}

function SupportStrip() {
  return (
    <section className="py-10 sm:py-14 border-t border-border/60">
      <div className="rounded-3xl border border-border bg-card/60 p-6 sm:p-10 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-5">
        <div className="max-w-xl">
          <h2 className="text-xl sm:text-2xl font-bold tracking-tight">
            Questions, or need an invite?
          </h2>
          <p className="mt-2 text-sm sm:text-base text-muted-foreground">
            We're a small team. Email us and we'll get back to you, usually within one
            business day.
          </p>
        </div>
        <div className="flex flex-wrap gap-3">
          <Link
            to="/support"
            className="rounded-full bg-primary text-primary-foreground px-5 py-2.5 text-sm font-semibold hover:opacity-90 transition"
          >
            Visit support
          </Link>
          <Link
            to="/support"
            className="rounded-full border border-border bg-background/60 px-5 py-2.5 text-sm font-semibold hover:bg-background transition"
          >
            Contact support
          </Link>
          <Link
            to="/privacy"
            className="rounded-full border border-border bg-background/60 px-5 py-2.5 text-sm font-semibold hover:bg-background transition"
          >
            Privacy policy
          </Link>
        </div>
      </div>
    </section>
  );
}

function CrawlerIndex() {
  return (
    <nav aria-label="Crawler index" style={{ position: "absolute", left: -9999, top: 0 }}>
      <a href="/privacy">Privacy</a>
      <a href="/support">Support</a>
      <a href="/terms">Terms</a>
      <a href="/account-deletion">Account Deletion</a>
      <a href="/login">Sign in</a>
    </nav>
  );
}
